import { createContext, useContext, useEffect, useMemo, useRef, ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { logSystemEvent, generateRequestId } from '@/lib/observability/logSystemEvent';
import { buildSystemEventContext } from '@/lib/observability/buildSystemEventContext';
import { setSupportPreviewActive } from '@/lib/supportPreview';
import posthog from 'posthog-js';

interface SupportPreviewContextValue {
  /** True when an admin is viewing the portal in read-only support mode */
  isSupportPreview: boolean;
}

const SupportPreviewContext = createContext<SupportPreviewContextValue>({ isSupportPreview: false });

/**
 * Provides support preview mode based on `?support_preview=1` query param.
 * Only activates for authenticated admins. Logs an audit event on activation.
 *
 * Side effects:
 * - Sets module-level flag for MutationCache guard (blocks all mutations)
 * - Registers PostHog super property `support_preview: true` for analytics tagging
 */
export function SupportPreviewProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const { isAdmin, user } = useAuth();
  const loggedRef = useRef<string | null>(null);

  const isSupportPreview = isAdmin && searchParams.get('support_preview') === '1';

  // Sync module-level flag for central mutation blocking
  useEffect(() => {
    setSupportPreviewActive(isSupportPreview);
    return () => setSupportPreviewActive(false);
  }, [isSupportPreview]);

  // Tag PostHog events so admin usage isn't counted as host usage
  useEffect(() => {
    if (isSupportPreview) {
      posthog.register({ support_preview: true, viewer_type: 'admin_support' });
    } else {
      posthog.unregister('support_preview');
    }
    return () => {
      posthog.unregister('support_preview');
    };
  }, [isSupportPreview]);

  // Audit log on activation (once per producer)
  useEffect(() => {
    if (!isSupportPreview || !user) return;
    const match = window.location.pathname.match(/\/producer\/farm\/([^/]+)/);
    const producerId = match?.[1];
    if (!producerId || producerId === loggedRef.current) return;
    loggedRef.current = producerId;

    logSystemEvent({
      level: 'info',
      event_key: 'admin_host_preview_opened',
      domain: 'admin',
      entity_type: 'producer',
      entity_id: producerId,
      producer_id: producerId,
      message: `Admin opened support preview for producer ${producerId}`,
      request_id: generateRequestId(),
      context: buildSystemEventContext({ feature: 'admin_preview', action: 'open', surface: 'support_preview', admin_user_id: user.id }),
    });
  }, [isSupportPreview, user]);

  const value = useMemo(() => ({ isSupportPreview }), [isSupportPreview]);

  return (
    <SupportPreviewContext.Provider value={value}>
      {children}
    </SupportPreviewContext.Provider>
  );
}

/** Check if current session is in admin support preview (read-only) mode */
export function useSupportPreview() {
  return useContext(SupportPreviewContext);
}
